import React from 'react'
import styled from '@emotion/styled'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import type { PromoType, PromoTypeClass } from '../../types'
import { promoTypeColorThemes } from '../../constants'

export const PromoSelectionSimple: React.FC<{
  title: string
  promoType: PromoType
  isSelected: boolean
  hasActiveSelection: boolean
  onClick: () => void
  setSelectionHeight: React.Dispatch<React.SetStateAction<number>>
  isPromoSelectorExpanded: boolean
  renderPosition: number
  descriptionSlots?: React.ReactNode[]
  style?: React.HTMLAttributes<HTMLDivElement>['style']
  isSingularDisplay?: boolean
}> = ({
  title,
  promoType,
  isSelected,
  hasActiveSelection,
  onClick,
  setSelectionHeight,
  isPromoSelectorExpanded,
  renderPosition,
  descriptionSlots,
  style,
  isSingularDisplay = false,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!ref.current || isSingularDisplay) return

    const selectionHeight = ref.current.getBoundingClientRect().height
    if (isSelected) {
      setSelectionHeight(selectionHeight)
    }

    if (!isPromoSelectorExpanded) {
      ref.current.style.transition = hasActiveSelection
        ? 'transform 0.4s ease, opacity 0.2s ease'
        : ''
      ref.current.style.transform = `translateY(${isSelected ? `-${selectionHeight * renderPosition}px` : '0px'})`
      ref.current.style.opacity = !hasActiveSelection || isSelected ? '1' : '0'
      ref.current.style.borderColor = isSelected ? colors.white : ''
    } else {
      ref.current.style.transform = 'translateY(0px)'
      ref.current.style.transition = hasActiveSelection
        ? 'transform 0.4s ease, opacity 0.3s ease 0.2s'
        : ''
      ref.current.style.opacity = '1'
      ref.current.style.borderColor = ''
    }
  }, [
    isSelected,
    setSelectionHeight,
    isPromoSelectorExpanded,
    renderPosition,
    hasActiveSelection,
    isSingularDisplay,
  ])

  const promoTypeClass: PromoTypeClass = `PromoType-${promoType}`

  return (
    <WrapperStyled style={style} className={promoTypeClass} ref={ref} onClick={onClick}>
      <div>
        <span>
          {promoType === 'Bonus' && (
            <Icon name='LineCurrencyDollarCircle' color={colors.celery[600]} size='1.6rem' />
          )}

          {promoType === 'KambiReward' && (
            <Icon name='LineGift01' color={colors.studio[600]} size='1.75rem' />
          )}
        </span>
      </div>

      <div>
        <h4>{title}</h4>

        {descriptionSlots?.map((slot, slotNum) => slot && <span key={slotNum}>{slot}</span>)}
      </div>

      <div>
        <RadioButton id={title} isChecked={isSelected} type='Tick' size='md' />
      </div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div(
  {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    fontFamily: font.family.primary,
    backgroundColor: colors.white,
    borderBottom: '0.1rem solid var(--light-color)',

    transition: 'transform 0.4s ease',
    transform: 'translateY(0px)',
    opacity: 1,

    ':last-of-type:not(:only-of-type)': { border: 0 },

    // Icon
    '> div:nth-of-type(1)': {
      '> span': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3.2rem',
        height: '3.2rem',
        borderRadius: radius.full,
        backgroundColor: 'var(--light-color)',
      },
    },

    // Title
    '> div:nth-of-type(2)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: spacing.smx2,

      '> h4': {
        margin: 0,
        padding: 0,
        color: colors.black,
        fontSize: font.size.md.fontSize,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: font.size.md.lineHeight,
        fontWeight: font.weight.medium,
      },

      '> span': {
        fontSize: font.size.sm.fontSize,
        letterSpacing: font.size.sm.letterSpacing,
        lineHeight: font.size.sm.lineHeight,
        fontWeight: font.weight.regular,
        color: colors.neutral[800],
      },
    },

    // Radio Button
    '> div:nth-of-type(3)': {
      marginLeft: 'auto',
    },
  },
  promoTypeColorThemes
)
